'use client'

import Image from 'next/image'
import { ou } from '@/lib/contenuEditable'

/**
 * Section "Événement Soirée Inaugurale" — pattern luxe 3 calques séparés,
 * jumeau de HomeHero pour cohérence sur tout le site.
 *
 *   1. Photo des danseurs (couple en valse, lustres dorés) — full bleed
 *   2. Title PNG transparent (Soirée Inaugurale + Paris 29 Mai)
 *   3. Bouton CTA principal
 *
 * Mobile : layout 2 zones (titre upper-mid / boutons lower)
 * Desktop : centré classique
 *
 * Mode `past` : bascule le bouton en "Revivez l'événement" pointant
 * vers l'ancre `#galerie` au lieu de `/#formules`. Le visuel (photo,
 * titre PNG, mise en page) reste identique — l'événement vit dans son
 * affiche, seul le CTA bascule en mode souvenir.
 */
interface EventHeroProps {
  mode?: 'upcoming' | 'past'
  content?: EventHeroContent
  /** Date lisible de la soirée, affichée quand la billetterie n'est pas ouverte. */
  dateAffichee?: string
}

/**
 * Contenu éditable depuis le CRM — section `evenement` de la page d'accueil.
 *
 * Le titre est une IMAGE, pas du texte : l'affiche du Bal est un lettrage
 * dessiné, qu'aucune police du site ne saurait reproduire. On rend donc le
 * fichier remplaçable, et son texte de remplacement modifiable pour les
 * lecteurs d'écran — mais pas la typographie elle-même.
 *
 * Le bouton a deux libellés, comme les autres blocs qui changent avec le
 * statut de l'événement : « Réserver ma place » avant la soirée,
 * « Revivez l'événement » après.
 */
export interface EventHeroContent {
  /* Mention affichée à la place du bouton tant que la billetterie n'est pas
     ouverte. Laissée vide, c'est la date de la soirée qui prend sa place —
     une affiche et une date disent déjà l'essentiel. */
  mentionBilletterie?: string
  /* Clé DISTINCTE de `backgroundImage`, et c'est délibéré : cette section
     portait déjà une valeur sous ce nom — /demo/placeholder.svg, une image qui
     contient DÉJÀ le lettrage. La reprendre comme fond superposait deux
     titres. Une clé nouvelle garantit que l'ancienne donnée ne peut plus
     détourner le champ. */
  photoFond?: string
  titleImage?: string
  titleAlt?: string
  primaryButton?: { label?: string; href?: string }
  primaryButtonPast?: { label?: string; href?: string }
}

const DEFAUTS = {
  photoFond: '/demo/placeholder.svg',
  titleImage: '/demo/placeholder.svg',
  titleAlt: "Soirée Inaugurale — Paris, 29 Mai",
  ctaLabel: 'Réserver ma place',
  ctaHref: '/#formules',
  ctaLabelPast: "Revivez l'événement",
  ctaHrefPast: '#galerie',
} as const

export default function EventHero({ mode = 'upcoming', content, dateAffichee }: EventHeroProps) {
  const isPast = mode === 'past'
  const photo = ou(content?.photoFond, DEFAUTS.photoFond)
  const titre = ou(content?.titleImage, DEFAUTS.titleImage)
  const titreAlt = ou(content?.titleAlt, DEFAUTS.titleAlt)
  const ctaLabel = isPast
    ? ou(content?.primaryButtonPast?.label, DEFAUTS.ctaLabelPast)
    : ou(content?.primaryButton?.label, DEFAUTS.ctaLabel)
  const ctaHref = isPast
    ? ou(content?.primaryButtonPast?.href, DEFAUTS.ctaHrefPast)
    : ou(content?.primaryButton?.href, '')

  /* ── Soirée annoncée, billetterie pas encore ouverte ──────────────────
     C'est l'ABSENCE de lien de réservation qui bascule l'affiche dans cet
     état : le jour où la billetterie ouvre, il suffit d'en renseigner
     l'adresse pour que le bouton revienne. Rien d'autre à faire, et aucun
     drapeau à penser à retirer.

     Un bouton grisé aurait été le réflexe : il met en avant ce qui manque
     plutôt que ce qui vient. On affiche donc soit la mention saisie, soit
     la date — une affiche et une date suffisent à annoncer. */
  const enAttenteDeBilletterie = !isPast && ctaHref === ''
  const mention = ou(content?.mentionBilletterie, '')
  return (
    <section
      id="evenement"
      className="relative w-full overflow-hidden bg-brand-cream scroll-mt-20"
      style={{ minHeight: '100vh' }}
    >
      <style>{`
        /* svh = small viewport height (figé, URL bar toujours réservée)
           → pas de "saut" des boutons quand l'URL bar mobile se retracte */
        @supports (height: 100svh) {
          .demo-event-full { min-height: 100svh !important; }
        }
        /* Titre : valeur mobile préservée, bumpée sur desktop ≥1024px */
        .demo-event-title { height: clamp(320px, 60vh, 680px); }
        @media (min-width: 1024px) {
          .demo-event-title { height: clamp(560px, 75vh, 900px); }
        }
        /* Photo : zoom mobile préservé (25%), recadré desktop pour montrer plus de la scène */
        .demo-event-bg-photo { object-position: center 25%; }
        @media (min-width: 1024px) {
          .demo-event-bg-photo { object-position: center 55% !important; }
        }
      `}</style>

      {/* 1️⃣ Photo de fond — danseurs (object-position remonté pour montrer les lustres sur desktop) */}
      <div className="absolute inset-0">
        <Image
          src={photo}
          alt=""
          fill
          quality={88}
          sizes="100vw"
          className="object-cover demo-event-bg-photo"
        />
        {/* Overlay double : un dégradé pour lisibilité du titre tout en gardant la photo lumineuse au bord */}
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse at center, rgba(0,0,0,0.20) 0%, rgba(0,0,0,0.45) 65%, rgba(0,0,0,0.70) 100%)',
          }}
        />
      </div>

      {/* 2️⃣ + 3️⃣ Titre + boutons */}
      <div
        className="demo-event-full relative z-10 flex flex-col items-center text-center px-4 md:px-10
                   justify-between md:justify-center
                   pt-[16vh] pb-[10vh] md:py-0"
        style={{ minHeight: '100vh' }}
      >
        {/* Titre PNG — Soirée Inaugurale, taille fluide */}
        <div className="md:mb-12">
          <Image
            src={titre}
            alt={titreAlt}
            width={1600}
            height={2000}
            className="demo-event-title w-auto h-auto"
            style={{
              maxWidth: '95vw',
              objectFit: 'contain',
              filter: 'drop-shadow(0 6px 24px rgba(0,0,0,0.55))',
            }}
          />
        </div>

        {/* Bouton CTA principal — affiche cérémonielle, un seul focus.
            En mode `past`, libellé "Revivez l'événement" + ancre #galerie. */}
        <div className="flex items-center justify-center">
          {enAttenteDeBilletterie ? (
            <div className="flex flex-col items-center gap-4">
              <span aria-hidden style={{ width: 48, height: 1, background: 'rgba(22, 36, 58,0.25)' }} />
              {mention !== '' ? (
                <span
                  className="text-brand-ink/85 uppercase"
                  style={{
                    fontFamily: 'var(--font-body, inherit)',
                    fontSize: 'clamp(10px, 0.6rem + 0.12vw, 12px)',
                    letterSpacing: '0.22em',
                  }}
                >
                  {mention}
                </span>
              ) : (
                dateAffichee && (
                  <span
                    className="font-normal text-brand-ink"
                    style={{
                      fontFamily: 'var(--font-heading, inherit)',
                      fontSize: 'clamp(1.25rem, 1rem + 1.2vw, 2.1rem)',
                      lineHeight: 1,
                    }}
                  >
                    {dateAffichee}
                  </span>
                )
              )}
            </div>
          ) : (
          <a href={ctaHref} className="gde-btn gde-btn--on-dark">
            {ctaLabel}
          </a>
          )}
        </div>
      </div>
    </section>
  )
}
